// Ishida pricing request: resolves the model named in the link, shows it, and describes the request for the existing lead path.
//
// What this file does NOT do: it never talks to HubSpot, never changes the form's action, and never touches routing. The form is a Shopify
// contact form; snippets/biztracker-lead-events.liquid intercepts the submit exactly as it does for every other lead. This script only
//   1. reads ?model= from the link and resolves it against the catalog STRICTLY (exact model name, or an exact "<model>, <configuration>" label;
//      anything else is left unresolved and recorded as typed, never guessed),
//   2. fills the visible model / configuration selections so the visitor can see and correct them,
//   3. on the lead snippet's bt:lead-prepare event, writes a plain-text "Quote Details" block and the confirmation sentence.

// Customer-facing capitalization: "Ishida UNI-7 Bench". The identifier (option values, URL parameter, message) stays "Uni-7 Bench".
export function displayModelName(n) {
  return String(n == null ? '' : n).replace(/^Uni-/, 'UNI-');
}

export function normaliseParam(raw) {
  return String(raw == null ? '' : raw).replace(/\+/g, ' ').replace(/\s+/g, ' ').trim();
}

// The label the site's own links use for a configuration: "Uni-5 Bench, 60 x 0.02 lb, RF" for a bench, "Astra II Pole" otherwise.
export function configurationLabel(model, cfg) {
  const name = String(model.model);
  const c = String(cfg.configuration || '');
  return name.endsWith(' Bench') && c.startsWith('Bench') ? name + c.slice('Bench'.length) : name + ' ' + c;
}

// { model, config, raw }. model is null when the link names nothing the catalog lists; config is null unless the label matched exactly.
export function resolveQuoteTarget(models, rawParam) {
  const raw = normaliseParam(rawParam);
  const result = { model: null, config: null, raw };
  if (!raw) return result;
  const lower = raw.toLowerCase();
  const eligible = (models || []).filter(m => m && m.finder_eligible === true);
  for (const m of eligible) {
    for (const c of (m.configurations || [])) {
      if (configurationLabel(m, c).toLowerCase() === lower) return { model: m, config: c, raw };
    }
  }
  const exact = eligible.find(m => String(m.model).toLowerCase() === lower);
  if (exact) return { model: exact, config: null, raw };
  return result;
}

export function headingFor(model) {
  return model ? 'Request Pricing for Ishida ' + displayModelName(model.model) : 'Request Pricing for Ishida Scales';
}

// The block the lead snippet appends to the HubSpot message (and the fallback email carries as a hidden field).
export function quoteDetails({ model, config, quantity, raw, page }) {
  const lines = ['Ishida pricing request'];
  lines.push('Model: ' + (model ? model.model : 'Not sure yet'));
  if (model && config) lines.push('Configuration: ' + config.configuration + (config.supplier_part_number ? ' (part ' + config.supplier_part_number + ')' : ''));
  else if (model && (model.configurations || []).length > 1) lines.push('Configuration: not chosen');
  lines.push('Quantity: ' + (quantity || 'not given'));
  if (raw && !model) lines.push('Model named in the link (not recognised): ' + raw);
  lines.push('Page: ' + (page || 'not recorded'));
  return lines.join('\n');
}

export function confirmationText({ model, config, quantity }) {
  let what = model ? 'Ishida ' + displayModelName(model.model) : 'Ishida scales';
  if (model && config) what += ' (' + config.configuration + ')';
  const qty = quantity ? ', quantity ' + quantity : '';
  return 'Thank you. We received your pricing request for ' + what + qty + '. A BizTracker specialist will reply to the email address you gave.';
}

const escapeHtml = s => String(s == null ? '' : s).replace(/[&<>"']/g, c => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));

function priceLine(model) {
  if (model && model.show_price === true && model.price && typeof model.price.amount !== 'undefined') {
    const n = Number(model.price.amount);
    if (isFinite(n)) {
      const prefix = model.price_prefix ? String(model.price_prefix).replace(/^./, c => c.toUpperCase()) + ' ' : '';
      return prefix + '$' + n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    }
  }
  return 'Contact for price';
}

function selectedCard(model, config, images) {
  if (!model) {
    return '<p class="ishida-quote__none">No model selected yet. Choose one in the form, or <a href="/pages/ishida-model-finder">use the Model Finder</a> to compare.</p>';
  }
  const photo = images && images[model.model] && images[model.model].url ? images[model.model] : null;
  const media = photo
    ? '<div class="ishida-quote__photo"><img src="' + escapeHtml(photo.url) + '" alt="' + escapeHtml(photo.alt || ('Ishida ' + displayModelName(model.model))) + '" width="' + Number(photo.width || 1200) + '" height="' + Number(photo.height || 900) + '" decoding="async"></div>'
    : '<div class="ishida-quote__plate" aria-hidden="true"><span>Ishida</span><strong>' + escapeHtml(displayModelName(model.model)) + '</strong></div>';
  const cfgLine = config
    ? '<p class="ishida-quote__config">' + escapeHtml(config.configuration) + (config.supplier_part_number ? ' <span class="ishida-model__pn">Part ' + escapeHtml(config.supplier_part_number) + '</span>' : '') + '</p>'
    : '';
  return media + '<h3 class="ishida-quote__model">Ishida ' + escapeHtml(displayModelName(model.model)) + '</h3>' + cfgLine +
    '<p class="ishida-quote__price">' + escapeHtml(priceLine(model)) + '</p>';
}

export function mount(root, catalog, order, images) {
  const models = (catalog.models || []).filter(m => m && m.finder_eligible === true);
  const form = root.querySelector('form');
  const modelSelect = root.querySelector('[data-ishida-quote-model]');
  const configWrap = root.querySelector('[data-ishida-quote-config-wrap]');
  const configSelect = root.querySelector('[data-ishida-quote-config]');
  const body = root.querySelector('[data-ishida-quote-selected-body]');
  const heading = document.documentElement.classList.contains('bt-ishida-quote') ? document.querySelector('.bt-hero__heading') : null;
  const qty = root.querySelector('[name="contact[Quantity]"]');
  if (!form || !modelSelect || !configSelect) return;
  form.classList.add('ishida-quote__form');

  const ordered = order && order.length
    ? order.map(n => models.find(m => m.model === n)).filter(Boolean).concat(models.filter(m => order.indexOf(m.model) < 0))
    : models;
  for (const m of ordered) {
    const o = document.createElement('option');
    o.value = m.model;
    o.textContent = 'Ishida ' + displayModelName(m.model);
    modelSelect.appendChild(o);
  }

  const baseTitle = document.title;
  const current = () => {
    const m = models.find(x => x.model === modelSelect.value) || null;
    const cfg = m ? (m.configurations || []).find(c => c.configuration === configSelect.value) || null : null;
    return { model: m, config: cfg };
  };
  const refreshConfigs = (preferred) => {
    const m = models.find(x => x.model === modelSelect.value) || null;
    while (configSelect.options.length > 1) configSelect.remove(1);
    const cfgs = m ? (m.configurations || []) : [];
    for (const c of cfgs) {
      const o = document.createElement('option');
      o.value = c.configuration;
      o.textContent = c.configuration + (c.supplier_part_number ? ' (part ' + c.supplier_part_number + ')' : '');
      configSelect.appendChild(o);
    }
    configSelect.value = preferred && cfgs.some(c => c.configuration === preferred) ? preferred : '';
    if (configWrap) configWrap.hidden = cfgs.length < 2;
  };
  const refresh = () => {
    const { model, config } = current();
    if (body) body.innerHTML = selectedCard(model, config, images);
    if (heading) heading.textContent = headingFor(model);
    document.title = model ? 'Request Pricing for Ishida ' + displayModelName(model.model) + ' | BizTracker' : baseTitle;
  };

  const params = new URLSearchParams(location.search);
  const target = resolveQuoteTarget(models, params.get('model'));
  if (target.model) {
    modelSelect.value = target.model.model;
    refreshConfigs(target.config ? target.config.configuration : '');
  } else {
    refreshConfigs('');
  }
  refresh();

  modelSelect.addEventListener('change', () => { refreshConfigs(''); refresh(); });
  configSelect.addEventListener('change', refresh);

  // The lead snippet dispatches this (cancelable) event on the form just before it builds the HubSpot message. Nothing is vetoed here. The
  // details are written at the top of the Message field: the shared HubSpot form has no fields of its own for the model, configuration and
  // quantity, and the message is what the snippet sends (and what the email fallback carries). The snippet itself is not changed.
  const message = form.querySelector('[name="contact[Message]"]');
  form.addEventListener('bt:lead-prepare', () => {
    if (!message) return;
    const { model, config } = current();
    const q = qty ? qty.value : '';
    if (typeof message.dataset.ishidaOriginal === 'undefined') message.dataset.ishidaOriginal = message.value;
    const block = quoteDetails({ model, config, quantity: q, raw: target.raw, page: location.pathname });
    message.value = block + (message.dataset.ishidaOriginal.trim() ? '\n\n' + message.dataset.ishidaOriginal.trim() : '');
  });
  // After a successful delivery the snippet shows its generic thank-you line; this names what was received.
  form.addEventListener('bt:lead-delivered', () => {
    const { model, config } = current();
    const done = form.querySelector('[data-bt-lead-success]');
    if (done) done.textContent = confirmationText({ model, config, quantity: qty ? qty.value : '' });
  });
}

// ------------------------------------------------------------------ replacement review (topic=ishida-replacement-review)
//
// The same request section, in its second mode. The visitor arrives from the Replacement Finder (or the replacement page) naming the scale they
// have. This shows what the catalog says about it, asks for the model, part or serial number and details, and sends through the same lead path.
// It never names a replacement: where the catalog documents none, it says so.

const keyOf = s => String(s == null ? '' : s).toUpperCase().replace(/[^A-Z0-9]/g, '');

// { kind: 'none' | 'legacy' | 'current' | 'unknown', raw, legacy?, model?, config?, part? }
export function resolveReviewTarget(catalog, rawValue) {
  const raw = normaliseParam(rawValue);
  if (!raw) return { kind: 'none', raw: '' };
  const key = keyOf(raw);
  const legacy = ((catalog && catalog.legacy) || []).find(l => keyOf(l && l.old_model) === key);
  if (legacy) return { kind: 'legacy', raw, legacy };
  const models = ((catalog && catalog.models) || []).filter(m => m && m.finder_eligible === true);
  const q = resolveQuoteTarget(models, raw);
  if (q.model) return { kind: 'current', raw, model: q.model, config: q.config, part: q.config ? q.config.supplier_part_number : null };
  for (const m of models) {
    const cfg = (m.configurations || []).find(c => keyOf(c.supplier_part_number) === key);
    if (cfg) return { kind: 'current', raw, model: m, config: cfg, part: cfg.supplier_part_number };
    const pn = (m.supplier_part_numbers || []).find(p => keyOf(p) === key);
    if (pn) return { kind: 'current', raw, model: m, config: null, part: pn };
  }
  return { kind: 'unknown', raw };
}

export function reviewHeading(target) {
  if (target && target.kind === 'legacy') return 'Request a Replacement Review for ' + target.legacy.old_model;
  if (target && target.kind === 'current') return 'Request a Replacement Review for ' + displayModelName(target.model.model);
  return 'Request a Replacement Review';
}

function legacyFact(l) {
  if (l.supplier_status === 'discontinued') {
    return 'listed as discontinued' + (l.supplier_status_observed_at ? ' (checked ' + l.supplier_status_observed_at + ')' : '');
  }
  return 'a record is held for this model';
}

// What the page says about the scale. Never a replacement: only that one is, or is not, documented.
export function reviewStatus(target) {
  const carry = ' A specialist confirms what carries over, such as labels, PLUs and interfaces, before recommending anything.';
  if (!target || target.kind === 'none') {
    return 'Enter the model printed on your scale’s plate. If you do not know it, describe the scale in the message and an Ishida specialist will identify it.';
  }
  if (target.kind === 'legacy') {
    const l = target.legacy;
    const named = l.current_replacement ? ' A possible replacement is on record; it is confirmed with you before it is recommended.' : ' No replacement is documented, so none is recommended.';
    return l.old_model + ': ' + legacyFact(l) + '.' + named + carry;
  }
  if (target.kind === 'current') {
    return displayModelName(target.model.model) + ' is a current Ishida model, so there is no replacement to show.' +
      ' If you are replacing an older scale, tell us which one.' + carry;
  }
  return 'We do not have a record for that model yet. An Ishida specialist will identify it from the details you send, and will not guess at a replacement.';
}

function onFile(target) {
  if (!target || target.kind === 'none') return 'no model given';
  if (target.kind === 'legacy') return legacyFact(target.legacy) + '; ' + (target.legacy.current_replacement ? 'a possible replacement is on record, to be confirmed' : 'no replacement documented');
  if (target.kind === 'current') return 'current Ishida model; no replacement to show';
  return 'no record held';
}

export function reviewDetails({ target, entered, part, quantity, page }) {
  const lines = ['Ishida replacement review'];
  lines.push('Scale being reviewed: ' + (entered || 'not given'));
  lines.push('Part or serial number: ' + (part || 'not given'));
  lines.push('Quantity: ' + (quantity || 'not given'));
  lines.push('On file: ' + onFile(target));
  lines.push('Page: ' + (page || 'not recorded'));
  return lines.join('\n');
}

export function reviewConfirmation({ entered }) {
  return 'Thank you. We received your replacement review request for ' + (entered ? entered : 'your scale') + '. A BizTracker specialist will reply to the email address you gave.';
}

export function mountReview(root, catalog) {
  const form = root.querySelector('form');
  const modelInput = root.querySelector('[name="contact[Current Scale Model]"]');
  const partInput = root.querySelector('[name="contact[Part or Serial Number]"]');
  const qty = root.querySelector('[name="contact[Quantity]"]');
  const status = root.querySelector('[data-ishida-review-status]');
  const heading = document.documentElement.classList.contains('bt-ishida-review') ? document.querySelector('.bt-hero__heading') : null;
  if (!form || !modelInput) return;
  form.classList.add('ishida-quote__form');
  const baseTitle = document.title;

  const show = target => {
    if (status) status.innerHTML = '<p>' + escapeHtml(reviewStatus(target)) + '</p>';
    if (heading) heading.textContent = reviewHeading(target);
    document.title = target.kind === 'legacy' || target.kind === 'current' ? reviewHeading(target) + ' | BizTracker' : baseTitle;
  };

  const params = new URLSearchParams(location.search);
  const linked = resolveReviewTarget(catalog, params.get('model') || params.get('replacing'));
  const typedFromLink = normaliseParam(params.get('model') || params.get('replacing'));
  if (!modelInput.value) {
    modelInput.value = linked.kind === 'current' ? displayModelName(linked.model.model) : linked.kind === 'legacy' ? linked.legacy.old_model : typedFromLink;
  }
  show(linked);

  if (modelInput.addEventListener) {
    modelInput.addEventListener('change', () => show(resolveReviewTarget(catalog, modelInput.value)));
  }

  const message = form.querySelector('[name="contact[Message]"]');
  form.addEventListener('bt:lead-prepare', () => {
    if (!message) return;
    const entered = String(modelInput.value || '').trim();
    const target = entered ? resolveReviewTarget(catalog, entered) : linked;
    if (typeof message.dataset.ishidaOriginal === 'undefined') message.dataset.ishidaOriginal = message.value;
    const block = reviewDetails({ target, entered, part: String((partInput && partInput.value) || '').trim(), quantity: qty ? qty.value : '', page: location.pathname });
    const own = message.dataset.ishidaOriginal.trim();
    message.value = block + (own ? '\n\n' + own : '');
  });
  form.addEventListener('bt:lead-delivered', () => {
    const done = form.querySelector('[data-bt-lead-success]');
    if (done) done.textContent = reviewConfirmation({ entered: String(modelInput.value || '').trim() });
  });
}

if (typeof document !== 'undefined') {
  const start = () => {
    const root = document.querySelector('[data-ishida-quote]');
    if (!root) return;
    // The section is on every contact page view, but it only does anything for an Ishida topic (the head script sets this class).
    // Any other contact link makes no catalog or presentation request here.
    if (!document.documentElement.classList.contains('bt-ishida-quote')) return;
    const src = root.getAttribute('data-catalog-url');
    const presentationSrc = root.getAttribute('data-presentation-url');
    let images = {};
    try {
      const el = root.querySelector('[data-ishida-image-urls]');
      images = el ? JSON.parse(el.textContent || '{}') : {};
    } catch (e) { images = {}; }
    if (!src) return;
    Promise.all([
      fetch(src).then(r => r.json()),
      presentationSrc ? fetch(presentationSrc).then(r => r.json()).catch(() => ({})) : Promise.resolve({})
    ]).then(([catalog, presentation]) => {
      if (document.documentElement.classList.contains('bt-ishida-review')) {
        const reviewRoot = document.querySelector('[data-ishida-review]');
        if (reviewRoot) mountReview(reviewRoot, catalog);
        return;
      }
      mount(root, catalog, Object.keys(presentation || {}), images);
    })
      .catch(() => { /* the generic selection stays; the form still works */ });
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start); else start();
}
